/**
 * API 요청·응답 스키마 — **서버와 클라이언트가 같은 정의를 본다.**
 *
 * 서버는 런타임 검증에, 클라이언트는 폼 검증과 타입에 쓴다. 한 벌만 두면
 * 계약이 어긋날 수 없다 — 서버가 필드를 바꾸면 클라이언트가 컴파일에서 깨진다.
 */

import { z } from 'zod';

import type { TimelineResponse } from './timeline.js';

import { SUPPORTED_LANGUAGES } from './language.js';
import { USER_ID_MAX_LENGTH, USER_ID_MIN_LENGTH, USER_ID_PATTERN } from './reservedIds.js';

export const languageSchema = z.enum(SUPPORTED_LANGUAGES);

/**
 * ⚠️ **예약어 검사는 여기 넣지 않는다.**
 *
 * 넣으면 예약어도 `VALIDATION_FAILED`로 뭉뚱그려져 클라이언트가 "형식이 틀렸다"와
 * "이미 쓸 수 없는 이름이다"를 구분해 안내할 수 없다. 서버는 `USER_ID_RESERVED`로
 * 판정하고, 클라이언트는 입력 중 즉시 피드백이 필요하면 `isReservedUserId()`를
 * 직접 부른다.
 */
export const userIdSchema = z
  .string()
  .min(USER_ID_MIN_LENGTH)
  .max(USER_ID_MAX_LENGTH)
  .regex(USER_ID_PATTERN, 'userId는 소문자로 시작하고 소문자·숫자·_·-만 쓸 수 있습니다');

export const userNameSchema = z.string().trim().min(1).max(30);

/**
 * 사용자가 올리는 URL.
 *
 * ⚠️ **`z.string().url()`만으로는 부족하다.** 그건 URL 파싱만 하므로
 * `javascript:alert(1)`·`data:text/html,...`·`vbscript:` 가 전부 통과한다.
 * 이 값은 공개 프로필에 렌더링되므로 `<a href>`에 들어가면 그대로 XSS가 된다.
 *
 * https만 허용한다 — 사이트가 https라 http 이미지는 어차피 혼합 콘텐츠로
 * 차단되고, 허용 스킴을 하나로 줄이면 판단할 거리가 없어진다.
 */
/**
 * loopback은 http를 허용한다.
 *
 * 브라우저 자신이 `http://localhost`를 **보안 컨텍스트로 취급한다**(W3C Secure
 * Contexts). 네트워크를 타지 않으니 가로채일 구간이 없어서다. 로컬 개발에서
 * 사진 저장소(MinIO)가 http인데 여기서 막으면, 개발할 때만 통째로 다른 경로를
 * 타게 되어 정작 검증하려던 흐름을 검증하지 못한다.
 */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

export const httpsUrlSchema = z
  .string()
  .max(500)
  .refine((value) => {
    try {
      const url = new URL(value);
      if (url.protocol === 'https:') {
        return true;
      }
      return url.protocol === 'http:' && LOOPBACK_HOSTS.has(url.hostname);
    } catch {
      return false;
    }
  }, 'https:// 로 시작하는 주소만 쓸 수 있습니다');

/** `cat_300182` 형식. 대역 검증은 서버가 카탈로그로 한 번 더 한다. */
export const categoryIdSchema = z.string().regex(/^cat_[123]\d{5}$/, 'categoryId 형식이 아닙니다');

/**
 * Pick 한 건. **이름만 필수고 사진·설명은 선택이다.**
 *
 * 사진을 강제하지 않는 이유: 고를 것은 정해졌는데 마땅한 사진이 없어서 등록을
 * 못 하는 상황이 생긴다. 격자에서 사진 없는 칸은 이름이 주인공인 글자 칸으로
 * 보이므로 줄이 깨지지도 않는다.
 *
 * 사진이 있다면 **업로드한 것만** 받는다 — 주소 직접 입력은 화면에서 없앴고,
 * 서버가 그 사용자가 올린 것인지 한 번 더 본다(핫링크·추적 픽셀 차단).
 */
export const pickInputSchema = z.object({
  categoryId: categoryIdSchema,
  itemName: z.string().trim().min(1).max(100),
  itemDescription: z.string().trim().max(200).optional(),
  itemImageUrl: httpsUrlSchema.optional(),
});
export type PickInput = z.infer<typeof pickInputSchema>;

// ── 이미지 업로드 ──

/**
 * 올릴 수 있는 형식.
 *
 * SVG는 받지 않는다 — 스크립트를 품을 수 있어서 같은 오리진에서 열면 XSS가 된다.
 * HEIC도 받지 않는다(브라우저가 못 그린다). 클라이언트가 캔버스로 줄이면서
 * 어차피 jpeg/webp로 바꿔 보낸다.
 */
export const UPLOADABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type UploadableImageType = (typeof UPLOADABLE_IMAGE_TYPES)[number];

/** 올릴 수 있는 최대 크기. 클라이언트가 줄여 보내므로 넉넉한 상한이다 */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const imageTicketRequestSchema = z.object({
  contentType: z.enum(UPLOADABLE_IMAGE_TYPES),
});
export type ImageTicketRequest = z.infer<typeof imageTicketRequestSchema>;

/**
 * 업로드 표. 브라우저가 이 값들로 **S3에 직접** 올린다.
 *
 * Lambda를 거치지 않는 이유: 파일이 Lambda를 통과하면 그 대역폭과 실행 시간이
 * 전부 비용이고, 페이로드 한도(6MB)에도 걸린다.
 *
 * presigned PUT이 아니라 POST인 이유: POST 정책에만 `content-length-range`가
 * 있다. PUT은 크기를 걸 수 없어 표 하나로 몇 GB든 올릴 수 있다.
 */
export interface ImageTicketResponse {
  /** 폼을 보낼 주소 */
  url: string;
  /** 파일보다 **먼저** 넣어야 하는 폼 필드들 */
  fields: Record<string, string>;
  /** 업로드가 끝나면 이 주소가 곧 itemImageUrl이 된다 */
  imageUrl: string;
  maxBytes: number;
}

/**
 * 한 번에 보낼 수 있는 Pick 수.
 * 비로그인 목록 이관이 주 용도라 넉넉하되, 무한정 받지는 않는다.
 */
export const PICK_BATCH_LIMIT = 100;

// ── 요청 ──

export const googleLoginRequestSchema = z.object({
  idToken: z.string().min(1),
});
export type GoogleLoginRequest = z.infer<typeof googleLoginRequestSchema>;

export const createProfileRequestSchema = z.object({
  userId: userIdSchema,
  userName: userNameSchema,
});
export type CreateProfileRequest = z.infer<typeof createProfileRequestSchema>;

export const searchUsersQuerySchema = z.object({
  q: z.string().trim().min(2).max(30),
});

/** 주소 바꾸기. 규칙은 가입할 때와 같다 */
export const changeHandleRequestSchema = z.object({
  handle: userIdSchema,
});
export type ChangeHandleRequest = z.infer<typeof changeHandleRequestSchema>;

/**
 * 프로필에 붙는 **한마디**.
 *
 * 60자로 끊는다. 소개글이 아니라 이름 밑에 한 줄로 붙는 말이라, 길면 줄이
 * 늘어나 프로필 카드가 밀린다. 짧게 쓰라고 강제하는 편이 "무엇을 쓸지"도
 * 쉽게 만든다 — 빈 칸이 넓으면 무엇을 채워야 할지 모른다.
 *
 * 줄바꿈은 받지 않는다. 한 줄로 그릴 자리에 여러 줄이 들어오면 카드가
 * 사람마다 다른 높이가 된다.
 */
export const MAX_BIO_LENGTH = 60;
export const bioSchema = z
  .string()
  .transform((text) => text.replace(/\s+/g, ' ').trim())
  .refine((text) => text.length <= MAX_BIO_LENGTH, {
    message: `bio must be ${MAX_BIO_LENGTH} characters or fewer`,
  });

export const updateProfileRequestSchema = z.object({
  userName: userNameSchema,
  /** 계정 잠그기. 보내지 않으면 그대로 둔다 */
  isPrivate: z.boolean().optional(),
  /**
   * 프로필 사진. **빈 문자열이면 지운다**, 없으면 그대로 둔다.
   *
   * `null`이 아니라 `''`인 이유: JSON에서 `null`과 "키 없음"은 자주 뒤섞이는데
   * (`JSON.stringify`가 `undefined`를 아예 빼버린다), 둘의 뜻이 정반대라
   * 헷갈리면 사진이 조용히 지워진다. 빈 문자열은 그럴 일이 없다.
   */
  imageUrl: z.string().max(500).optional(),
  /**
   * 프로필의 한마디. **빈 문자열이면 지운다**, 없으면 그대로 둔다
   * (`imageUrl`과 같은 규칙이다).
   */
  bio: bioSchema.optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const putPickRequestSchema = pickInputSchema.omit({ categoryId: true });
export type PutPickRequest = z.infer<typeof putPickRequestSchema>;

export const putPicksRequestSchema = z.object({
  picks: z.array(pickInputSchema).min(1).max(PICK_BATCH_LIMIT),
});
export type PutPicksRequest = z.infer<typeof putPicksRequestSchema>;

/**
 * 목록 순서. **전체를 보낸다.**
 *
 * 옮긴 것만 보내면 서버가 나머지 자리를 추측해야 하고, 그 추측이 화면과
 * 어긋나면 사용자는 방금 옮긴 것이 제자리로 돌아간 것처럼 본다.
 * 통째로 보내면 같은 요청을 두 번 보내도 결과가 같다.
 */
export const reorderPicksRequestSchema = z.object({
  categoryIds: z.array(categoryIdSchema).min(1).max(PICK_BATCH_LIMIT),
});
export type ReorderPicksRequest = z.infer<typeof reorderPicksRequestSchema>;

export const categoryRequestSchema = z.object({
  text: z.string().trim().min(1).max(50),
  note: z.string().trim().max(300).optional(),
  language: languageSchema,
});
export type CategoryRequestInput = z.infer<typeof categoryRequestSchema>;

// ── 응답 ──

/**
 * 프로필에 **언어가 없다.**
 *
 * 화면은 언제나 **보는 사람의** 언어로 그려진다 — 카테고리 이름은 클라이언트가
 * 자기 언어 파일로 해석하고, 사용자가 쓴 글은 어차피 번역하지 않는다. 그래서
 * 프로필에 저장된 언어는 무엇을 그릴지에 아무 영향이 없었고, 로그인할 때마다
 * 맞춰 주는 동기화 경로만 남아 있었다(그 경로에서 로그인이 막히는 버그도 났다).
 *
 * 화면 언어는 브라우저가 기억한다(LocalStorage → navigator.language → en).
 */
/**
 * ⚠️ **내부 id(`userId`)를 담지 않는다.**
 *
 * 가입할 때 고른 주소가 내부 id가 되므로, 주소를 바꿔도 내부 id에는 **옛
 * 주소가 그대로 남는다.** 그걸 응답에 실으면 이름이 박힌 주소를 버리고
 * 바꾼 사람의 옛 주소가 공개 프로필에서 그대로 읽힌다 — 바꾼 의미가 없다.
 *
 * 바깥은 `handle`만 안다. 내부 id는 서버 안에서만 쓴다.
 */
export interface ProfileResponse {
  /** 공개 주소 — `life-timeline.com/@<handle>` */
  handle: string;
  /**
   * 잠긴 계정인가. **기본은 공개다.**
   *
   * 남의 프로필에도 실려 나간다 — 화면이 자물쇠를 그리고 "팔로우하면 볼 수
   * 있다"고 말하려면 이 값이 필요하다. 숨길 것도 아니다.
   */
  isPrivate: boolean;
  userName: string;
  imageUrl?: string;
  /** 이름 밑에 붙는 한마디. 안 쓴 사람은 없다 */
  bio?: string;
  createdAt: string;
}

export interface PickResponse {
  /** 이름이 아니라 id를 준다 — 보는 사람의 언어로 클라이언트가 해석한다 */
  categoryId: string;
  itemName: string;
  itemDescription?: string;
  itemImageUrl?: string;
  /** 사용자가 정한 자리. 목록은 이미 이 순서로 정렬돼 온다 */
  position?: number;
  updatedAt: string;
}

export interface PicksResponse {
  picks: PickResponse[];
}

/**
 * 내가 저 사람과 어떤 사이인가 — 공개 프로필(`life-timeline.com/@junho`)이 쓴다.
 *
 * **관계는 서로 맺는다.** 한쪽이 신청하고 상대가 수락하면 **둘 다** 이어진
 * 사이가 된다(링크드인 1촌). 따라붙는 쪽과 따라붙힌 쪽이 따로 없다 — 그래서
 * 이어진 뒤에는 누가 먼저 신청했는지가 화면에서 아무 차이도 만들지 않는다.
 *
 * | 값 | 뜻 | 화면이 그리는 것 |
 * |---|---|---|
 * | `self` | 나 자신 | 없음 |
 * | `connected` | 이어짐 | 끊기 |
 * | `outgoing` | 내가 신청해 두고 기다리는 중 | 신청 취소 |
 * | `incoming` | 상대가 신청해서 내 대답을 기다리는 중 | 수락 · 거절 |
 * | `none` | 아무 사이 아님 | 신청 |
 */
export type Relation = 'self' | 'connected' | 'outgoing' | 'incoming' | 'none';

/** 관계 상태. 응답에 그대로 나가므로 여기가 정본이다 */
export type ConnectionStatus = 'pending' | 'accepted';

/**
 * 관계 한 줄. **보는 사람 기준**이다 — `direction`은 내가 신청했는지
 * 상대가 신청했는지를 말한다.
 */
export interface ConnectionResponse {
  handle: string;
  userName: string;
  imageUrl?: string;
  status: ConnectionStatus;
  /** `outgoing` = 내가 신청함, `incoming` = 상대가 신청함 */
  direction: 'outgoing' | 'incoming';
  requestedAt: string;
}

/** 인연 목록 조회. 상태를 따로 묻고 커서로 이어 읽는다 */
export const listConnectionsQuerySchema = z.object({
  status: z.enum(['accepted', 'pending']),
  cursor: z.string().max(2000).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type ListConnectionsQuery = z.infer<typeof listConnectionsQuerySchema>;

/**
 * 프로필 맨 위에 찍는 두 숫자.
 *
 * 목록을 받아 세지 않는 이유: 목록은 한 쪽만 온다. 세는 일은 서버가 한다.
 */
export interface ConnectionSummaryResponse {
  connected: number;
  /** 내 대답을 기다리는 **받은** 요청 수 */
  incoming: number;
}

export interface ConnectionsResponse {
  connections: ConnectionResponse[];
  /**
   * 이어 읽을 자리. 없으면 끝이다.
   *
   * 몇 번째부터가 아니라 **어디서부터**다 — 보는 동안 앞에서 관계가 생기거나
   * 사라져도 건너뛰거나 두 번 보이지 않는다(번호로 세면 그렇게 된다).
   */
  nextCursor: string | null;
}

/**
 * 분석이 "최근"이라고 부르는 기간.
 *
 * 조회 기록은 **90일 뒤 TTL이 지운다.** 창을 그보다 좁게 잡으면 들고 있는
 * 기록의 일부만 세게 되는데, 버리지도 않을 것을 굳이 빼고 셀 이유가 없다 —
 * 그래서 창을 보관 기간과 같은 90일로 맞춘다(링크드인도 90일이다).
 *
 * 둘이 같으므로 지금은 "최근 90일 수 = 전체 수"다. 화면의 '전체' 줄은 둘이
 * 다를 때만 나오므로 저절로 숨는다 — 나중에 보관을 늘리면 그때 되살아난다.
 */
export const RECENT_VIEW_DAYS = 90;

export interface ViewerResponse {
  handle: string;
  userName: string;
  imageUrl?: string;
  lastViewedAt: string;
}

export interface ViewersResponse {
  /** 남아 있는 기록 전부 */
  count: number;
  /** 최근 {@link RECENT_VIEW_DAYS}일 안에 본 사람 수 */
  recentCount: number;
  /** 무료면 목록이 잠긴다 */
  locked: boolean;
  /** 잠겨 있으면 `null`. **이름을 보내 놓고 가리지 않는다** */
  viewers: ViewerResponse[] | null;
}

export interface UserSummaryResponse {
  handle: string;
  userName: string;
  imageUrl?: string;
}

/**
 * 사람 찾기 결과.
 *
 * **쪽을 넘기지 않는다.** 두 인덱스(주소·이름)를 각각 읽어 합치는 구조라
 * 커서가 둘이 되고, 합친 뒤 중복을 빼므로 "다음 20명"이 20명이 아닐 수도
 * 있다. 그 복잡도를 감수할 만큼 넘겨 볼 일이 잦지도 않다 — 사람들은 다음
 * 쪽을 누르는 대신 **글자를 더 친다.**
 *
 * 대신 잘렸다는 것은 **반드시 알린다.** 조용히 스무 명에서 자르면 그게
 * 전부인 줄 알고 더 찾기를 포기한다.
 */
export interface SearchUsersResponse {
  users: UserSummaryResponse[];
  /** 더 있는데 안 보여준 것이 있는가. 있으면 더 좁혀 달라고 말한다 */
  hasMore: boolean;
}

/**
 * 남의 인연 목록 — `/@junho`의 인연 수를 누르면 이게 온다.
 *
 * ⚠️ {@link ConnectionsResponse}와 **일부러 다른 타입**이다. 여기에는
 * `direction`도 `requestedAt`도 없다 — 누가 먼저 신청했는지는 그 둘 사이의
 * 일이라 제삼자가 알 일이 아니다. 이름과 사진, 그리고 눌러서 갈 주소면
 * "누가 있는지"라는 질문에 답이 된다.
 *
 * 프로필 본문과 **같은 잣대로 가려진다** — 잠긴 계정의 인연은 이어진
 * 사람에게만 열린다.
 */
export interface ConnectedPeopleResponse {
  /**
   * 전부 몇 명인가. **한 쪽만 읽어 와도 아는 수**다 — 프로필 줄에 세어 둔
   * 값이라 여기서 더 읽는 비용이 0이다(어차피 프로필을 열어 권한을 본다).
   */
  count: number;
  people: UserSummaryResponse[];
  nextCursor: string | null;
}

/**
 * 남의 프로필.
 *
 * `timeline`과 `picks`는 **볼 수 있을 때만** 채워진다. 못 볼 때는 `null`이다 —
 * 빈 배열로 주면 "기록이 없는 사람"과 "내가 못 보는 사람"이 구분되지 않는다.
 */
export interface PublicProfileResponse {
  profile: ProfileResponse;
  relation: Relation;
  /**
   * 이 사람이 맺은 인연 수.
   *
   * 잠긴 계정에서도 내려준다 — 숫자 자체는 누구인지 말해 주지 않고, 얼마나
   * 이어져 있는 사람인지가 신청할지 정하는 데 쓰인다. **누구인지**는
   * `/api/profiles/:handle/connections`가 답하고, 그쪽은 프로필 본문과 같은
   * 잣대로 가려진다.
   */
  connectionCount: number;
  timeline: TimelineResponse | null;
  picks: PickResponse[] | null;
}

export interface LoginResponse {
  token: string;
  /**
   * 계정이 이미 있으면 프로필, 없으면 null.
   *
   * 이 값이 클라이언트의 분기를 결정한다 — null이면 가입 화면, 있으면
   * 로컬 목록과의 병합 판정으로 간다(계획 §9).
   */
  user: ProfileResponse | null;
}

export interface CategoryRequestResponse {
  /** 같은 요청이 지금까지 몇 번 들어왔는지 */
  count: number;
}
